import {
  AdvertisersByDayWidgetResponse,
  AdvertisersByDomainWidgetResponse,
  MyAdvertisers,
  Advertiser,
  Campaign,
  AdvertisersBudgetInfoResponse,
  AdvertisersEpisodeLevelWidgetResponse,
} from 'api/services';
import { orderBy } from 'lodash';
import flatMap from 'lodash/flatMap';
import flow from 'lodash/flow';
import map from 'lodash/map';
import partialRight from 'lodash/partialRight';
import { displayDate } from 'utils/date';

export function promoDailyStatsDataSelector(d: AdvertisersByDayWidgetResponse) {
  return d.data?.map((dailyStat) => ({
    date: displayDate(dailyStat.dayMillis),
    dateMillis: dailyStat.dayMillis,
    adPlayed: dailyStat.spResultPlayed,
    adClickThrough: dailyStat.spClickThrough,
    avgCpc: dailyStat.avgCpcMilliCent,
    spEngaged: dailyStat.spEngaged,
    total: dailyStat.totalCostMilliCent,
    unitLoad: dailyStat.unitLoad,
    unitInViewport: dailyStat.unitInViewport,
  }));
}

export function promoByDomainCompleteStatsDataSelector(
  d: AdvertisersByDomainWidgetResponse,
) {
  return orderBy(
    d.data?.map((dailyStat) => ({
      displayName: dailyStat.displayName,
      sourceType: dailyStat.sourceType,
      domain: dailyStat.domainInfo?.domain ?? '',
      appDisplayName: dailyStat.appInfo?.displayName ?? '',
      bundleId: dailyStat.appInfo?.bundleId ?? '',
      platform: dailyStat.appInfo?.platform ?? '',
      adPlayed: dailyStat.spResultPlayed,
      adClickThrough: dailyStat.spClickThrough,
      avgCpc: dailyStat.avgCpcMilliCent,
      spEngaged: dailyStat.spEngaged,
      total: dailyStat.totalCostMilliCent,
      unitLoad: dailyStat.unitLoad,
      unitInViewport: dailyStat.unitInViewport,
    })),
    'adPlayed',
    'desc',
  );
}

export const promoByDomainTopSitesStatsDataSelector =
  (maxDisplayCount: number) => (d: AdvertisersByDomainWidgetResponse) =>
    orderBy(
      d.data?.map((dailyStat) => ({
        displayName: dailyStat.displayName,
        sourceType: dailyStat.sourceType,
        domain: dailyStat.domainInfo?.domain ?? '',
        appDisplayName: dailyStat.appInfo?.displayName ?? '',
        bundleId: dailyStat.appInfo?.bundleId ?? '',
        platform: dailyStat.appInfo?.platform ?? '',
        adPlayed: dailyStat.spResultPlayed,
        adClickThrough: dailyStat.spClickThrough,
        avgCpc: dailyStat.avgCpcMilliCent,
        spEngaged: dailyStat.spEngaged,
        total: dailyStat.totalCostMilliCent,
        unitLoad: dailyStat.unitLoad,
        unitInViewport: dailyStat.unitInViewport,
      })),
      'spEngaged',
      'desc',
    )
      .filter((stat) => !!stat.spEngaged)
      .slice(0, maxDisplayCount);

export function promoEpisodeLevelDataSelector(
  d: AdvertisersEpisodeLevelWidgetResponse,
) {
  return d.data?.map((episodeStat) => ({
    episodeName: episodeStat.episode.title,
    adPlayed: episodeStat.spResultPlayed,
    adClickThrough: episodeStat.spClickThrough,
    avgCpc: episodeStat.avgCpcMilliCent,
    spEngaged: episodeStat.spEngaged,
    spEngagedRate: episodeStat.unitInViewport
      ? episodeStat.spEngaged / episodeStat.unitInViewport
      : 0,
    total: episodeStat.totalCostMilliCent,
    unitLoad: episodeStat.unitLoad,
    unitInViewport: episodeStat.unitInViewport,
  }));
}

export function promoCampaingsBudgetSelector(d: AdvertisersBudgetInfoResponse) {
  return {
    totalCostBudgetMilliCent: d.totalCostBudgetMilliCent,
    totalCostBudgetUsedMilliCent: d.totalCostBudgetUsedMilliCent,
  };
}

export function campaignIdsSelector(d: MyAdvertisers) {
  return flow(
    partialRight<Advertiser[], string, Campaign[]>(flatMap, 'campaigns'),
    partialRight<Campaign[], string, string[]>(map, 'campaignSid'),
  )(d.advertisers);
}

export function campaignsSelector(d: MyAdvertisers) {
  return flatMap(d.advertisers, 'campaigns');
}
